import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { getAuth } from "../utils/auth";
import { userAPI } from "../services/api";
import { packageService } from "../services/packageService";
import { FiLock, FiArrowRight, FiStar, FiZap, FiCheckCircle } from "react-icons/fi";

export default function TierFeatureGuard({ featureKey, featureName = "Page Feature", requireActiveSubOnly = false, children }) {
  const [auth, setAuth] = useState(getAuth);
  const [profile, setProfile] = useState(null);
  const [targetPackage, setTargetPackage] = useState(null);
  const [targetTierName, setTargetTierName] = useState("");
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [profRes, pkgData] = await Promise.all([
          userAPI.getProfile().catch(() => null),
          packageService.fetchPackages().catch(() => null)
        ]);

        if (profRes?.data?.success && profRes.data.data) {
          setProfile(profRes.data.data);
        }

        const packages = pkgData?.packages || [];
        if (featureKey) {
          const matchingPkgs = packages.filter(p => {
            let feats = p.allowedFeatures || p.allowedFeaturesJson || p.AllowedFeatures || p.AllowedFeaturesJson;
            if (typeof feats === "string") {
              try { feats = JSON.parse(feats); } catch {}
            }
            return Array.isArray(feats) && feats.includes(featureKey);
          });

          if (matchingPkgs.length > 0) {
            matchingPkgs.sort((a, b) => (Number(a.price) || 0) - (Number(b.price) || 0));
            const match = matchingPkgs[0];
            setTargetPackage(match);
            const tName = match.name || match.packageType || match.Name || match.PackageType || "Higher Tier";
            setTargetTierName(tName);
          } else {
            setTargetPackage(null);
            setTargetTierName("Higher Tier");
          }
        }
      } catch (err) {
        console.warn("Failed to fetch data in TierFeatureGuard:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [featureKey]);

  const isAdmin = (auth?.role || "").toLowerCase().includes("admin");
  if (isAdmin) return children;

  const activeSub = profile?.activeSubscription;
  const isSubActive = activeSub && 
    (activeSub.status === "Active" || activeSub.status === "Approved") && 
    (new Date(activeSub.endDate) > new Date() || (typeof activeSub.daysLeft === "number" && activeSub.daysLeft > 0));
  
  const userTierName = isSubActive
    ? (activeSub?.packageName || profile?.tier || auth?.user?.tier || "Free Trial")
    : "Expired Plan";

  if (loading) {
    return (
      <div className="min-h-[70vh] flex items-center justify-center p-6">
        <div className="flex flex-col items-center gap-3">
          <div className="w-10 h-10 border-3 border-amber-500 border-t-transparent rounded-full animate-spin" />
          <p className="text-[13px] font-bold text-zinc-500">Checking subscription status...</p>
        </div>
      </div>
    );
  }

  // ── CASE 1: SUBSCRIPTION / FREE TRIAL EXPIRED ─────────────────────────────
  if (!isSubActive) {
    return (
      <div className="min-h-[82vh] flex items-center justify-center p-4 sm:p-8">
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 15 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          transition={{ duration: 0.35, ease: [0.25, 1, 0.5, 1] }}
          className="relative max-w-xl w-full rounded-[32px] p-8 sm:p-10 text-center overflow-hidden border border-rose-950/15"
          style={{
            background: "linear-gradient(145deg, rgba(255,255,255,0.96), rgba(254,242,242,0.98))",
            boxShadow: "0 25px 70px rgba(225,29,72,0.12)",
          }}
        >
          {/* Top Decorative Red Glow */}
          <div className="absolute -top-20 left-1/2 -translate-x-1/2 w-60 h-60 rounded-full bg-rose-400/20 blur-3xl pointer-events-none" />

          <div className="relative z-10 flex flex-col items-center">
            {/* Lock Badge Icon */}
            <div className="w-16 h-16 rounded-[24px] bg-gradient-to-br from-rose-500 to-red-600 flex items-center justify-center text-white shadow-lg mb-6 border border-rose-300/40">
              <FiLock size={28} />
            </div>

            {/* Expired Status Pill */}
            <div className="inline-flex items-center gap-1.5 px-3.5 py-1 rounded-full bg-rose-100/90 text-rose-900 border border-rose-300/60 text-[11px] font-extrabold mb-3">
              <FiStar size={12} className="text-rose-600" />
              <span>Subscription Status: Expired</span>
            </div>

            {/* Main Title */}
            <h2 className="text-[24px] sm:text-[28px] font-black text-zinc-900 tracking-tight mb-2" style={{ fontFamily: "'Poppins', sans-serif" }}>
              Renew Subscription to Access {featureName}
            </h2>

            <p className="text-[13.5px] text-zinc-600 font-medium leading-relaxed max-w-md mb-6">
              Your subscription plan has expired. Renew your plan to restore full access to live sessions, practice video library, diet charts, and member features!
            </p>

            {/* Features Benefits List */}
            <div className="w-full rounded-[20px] bg-white/90 p-4 mb-7 border border-rose-950/10 text-left space-y-2.5 shadow-2xs">
              <p className="text-[10.5px] font-black uppercase tracking-wider text-rose-700 mb-2">
                Renewal Restoration Benefits:
              </p>
              {[
                `Instant full restoration of ${featureName}`,
                "Access to Live Classes & On-Demand Video Library",
                "Personalized Diet Plans & Analytics Tracking",
                "Instant activation upon renewal"
              ].map((b, idx) => (
                <div key={idx} className="flex items-center gap-2.5 text-[12px] font-bold text-zinc-800">
                  <FiCheckCircle size={14} className="text-emerald-600 shrink-0" />
                  <span>{b}</span>
                </div>
              ))}
            </div>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row items-center gap-3 w-full">
              <Link
                to="/user/profile?tab=subscription"
                className="w-full sm:flex-1 h-12 rounded-full text-white font-extrabold text-[13.5px] flex items-center justify-center gap-2 shadow-lg hover:opacity-95 transition-all text-decoration-none"
                style={{ background: "linear-gradient(135deg,#E11D48,#BE123C)", boxShadow: "0 8px 24px rgba(225,29,72,0.35)" }}
              >
                <FiZap size={16} /> Renew / Upgrade Subscription <FiArrowRight size={15} />
              </Link>

              <button
                onClick={() => navigate("/user/dashboard")}
                className="w-full sm:w-auto h-12 px-6 rounded-full text-[13px] font-bold bg-zinc-100 hover:bg-zinc-200 text-zinc-700 border border-zinc-200 cursor-pointer transition-colors"
              >
                Back to Dashboard
              </button>
            </div>
          </div>
        </motion.div>
      </div>
    );
  }

  // ── CASE 2: ACTIVE SUBSCRIPTION CORE RANK FEATURE ─────────────────────────
  if (requireActiveSubOnly) {
    return children;
  }

  // ── CASE 3: ACTIVE SUBSCRIPTION ADMIN-TOGGLED FEATURE ──────────────────────
  const allowedFeatures = activeSub?.allowedFeatures || profile?.activeSubscription?.allowedFeatures || profile?.allowedFeatures;
  let isAllowed = true;
  if (allowedFeatures !== undefined && allowedFeatures !== null && Array.isArray(allowedFeatures)) {
    isAllowed = allowedFeatures.includes(featureKey);
  }

  if (!isAllowed) {
    const upgradeLabel = targetTierName ? `Upgrade to ${targetTierName}` : "Upgrade Plan Tier";

    // Helper to parse features bullet points defined by Admin on package creation
    const parseFeatures = (pkg) => {
      if (!pkg) return [];
      const raw = pkg.featuresJson || pkg.featuresText || pkg.features || pkg.FeaturesJson || pkg.FeaturesText || pkg.Features;
      if (!raw) return [];
      if (Array.isArray(raw)) return raw.filter(Boolean);
      if (typeof raw === "string") {
        try {
          const parsed = JSON.parse(raw);
          if (Array.isArray(parsed)) return parsed.filter(Boolean);
        } catch {}
        return raw.split(/[\r\n]+/).map(s => s.trim().replace(/^[-•*]\s*/, "")).filter(Boolean);
      }
      return [];
    };

    const adminBullets = parseFeatures(targetPackage);
    const displayBullets = adminBullets.length > 0
      ? adminBullets
      : [
          `Full unlimited access to ${featureName}`,
          `Included in ${targetTierName}`,
          "Instant activation upon plan upgrade"
        ];

    return (
      <div className="min-h-[82vh] flex items-center justify-center p-4 sm:p-8">
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 15 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          transition={{ duration: 0.35, ease: [0.25, 1, 0.5, 1] }}
          className="relative max-w-xl w-full rounded-[32px] p-8 sm:p-10 text-center overflow-hidden border border-amber-950/15"
          style={{
            background: "linear-gradient(145deg, rgba(255,255,255,0.95), rgba(254,249,240,0.98))",
            boxShadow: "0 25px 70px rgba(242,154,12,0.12)",
          }}
        >
          {/* Top Decorative Glow */}
          <div className="absolute -top-20 left-1/2 -translate-x-1/2 w-60 h-60 rounded-full bg-amber-400/20 blur-3xl pointer-events-none" />

          <div className="relative z-10 flex flex-col items-center">
            {/* Lock Badge Icon */}
            <div className="w-16 h-16 rounded-[24px] bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center text-white shadow-lg mb-6 border border-amber-300/40">
              <FiLock size={28} />
            </div>

            {/* Current Tier Pill */}
            <div className="inline-flex items-center gap-1.5 px-3.5 py-1 rounded-full bg-amber-100/80 text-amber-900 border border-amber-300/60 text-[11px] font-extrabold mb-3">
              <FiStar size={12} className="text-amber-600" />
              <span>Current Plan: {userTierName}</span>
            </div>

            {/* Main Title */}
            <h2 className="text-[24px] sm:text-[28px] font-black text-zinc-900 tracking-tight mb-2" style={{ fontFamily: "'Poppins', sans-serif" }}>
              Unlock {featureName}
            </h2>

            <p className="text-[13.5px] text-zinc-600 font-medium leading-relaxed max-w-md mb-6">
              Access to <strong className="text-zinc-900">{featureName}</strong> is not enabled on your <strong className="text-zinc-800">{userTierName}</strong>. Upgrade to <strong className="text-amber-800 font-bold">{targetTierName}</strong> to unlock full feature access!
            </p>

            {/* Dynamic Admin Package Benefits List */}
            <div className="w-full rounded-[20px] bg-white/90 p-4 mb-7 border border-amber-950/10 text-left space-y-2.5 shadow-2xs">
              <p className="text-[10.5px] font-black uppercase tracking-wider text-amber-700 mb-2">
                {targetTierName} Member Benefits:
              </p>
              {displayBullets.map((b, idx) => (
                <div key={idx} className="flex items-center gap-2.5 text-[12px] font-bold text-zinc-800">
                  <FiCheckCircle size={14} className="text-emerald-600 shrink-0" />
                  <span>{b}</span>
                </div>
              ))}
            </div>

            {/* CTA Buttons - Redirects directly to Profile Page -> Subscription Tab */}
            <div className="flex flex-col sm:flex-row items-center gap-3 w-full">
              <Link
                to="/user/profile?tab=subscription"
                className="w-full sm:flex-1 h-12 rounded-full text-white font-extrabold text-[13.5px] flex items-center justify-center gap-2 shadow-lg hover:opacity-95 transition-all text-decoration-none"
                style={{ background: "linear-gradient(135deg,#F29A0C,#E97818)", boxShadow: "0 8px 24px rgba(242,154,12,0.35)" }}
              >
                <FiZap size={16} /> {upgradeLabel} <FiArrowRight size={15} />
              </Link>

              <button
                onClick={() => navigate("/user/dashboard")}
                className="w-full sm:w-auto h-12 px-6 rounded-full text-[13px] font-bold bg-zinc-100 hover:bg-zinc-200 text-zinc-700 border border-zinc-200 cursor-pointer transition-colors"
              >
                Back to Dashboard
              </button>
            </div>
          </div>
        </motion.div>
      </div>
    );
  }

  return children;
}
